import { exists } from 'common/functions';
import { uniqueId } from 'lodash';
import { CellRenderer, DataRow, DataValue } from 'plugins/types';
import { FIELD_TYPE, SchemaField, SchemaFields } from 'schemas';
import { plugin } from '../plugin';
import { TooltipPlacement, TruncateWithTooltip } from '../components';
import { EmptyFieldRenderer } from '../field-renderers';

import './SmartFieldCellRenderer.scss';

const MAX_ITEMS = 3;

const getFieldRenderer = ({
  fieldId,
  field,
  row,
  schemaFields,
  value,
}: {
  fieldId: string;
  field: SchemaField;
  row: DataRow;
  schemaFields: SchemaFields | undefined;
  value: DataValue;
}): JSX.Element => {
  const Renderer = plugin.fieldRenderers?.[field.type];

  if (Renderer) {
    return <Renderer fieldId={fieldId} row={row} schemaFields={schemaFields} value={value} />;
  }

  const fieldType = [FIELD_TYPE.NUMBER, FIELD_TYPE.BOOLEAN].includes(field.type) ? field.type : 'string';
  return (
    <span data-term-field={fieldId} data-term-value-type={fieldType}>
      <TruncateWithTooltip
        id={uniqueId(`SmartFieldRenderer-${value}-`)}
        text={value?.toString() ?? ''}
        placement={TooltipPlacement.BOTTOM}
      />
    </span>
  );
};

export const SmartFieldCellRenderer: CellRenderer = ({ fieldId, row, schemaFields }) => {
  const relatedFieldIds = schemaFields?.[fieldId]?.relatedFields ?? [];
  const relatedFields = relatedFieldIds.reduce<SchemaFields>((acc, id) => {
    const field = schemaFields?.[id];

    if (field) {
      return { ...acc, [id]: field };
    }

    return acc;
  }, {});
  const renderers = Object.entries(relatedFields)
    .map(([id, field]) => {
      const value = row[id];

      if (!value) {
        return null;
      }

      // Check if the field value if of type array and if yes, then render them as individual items
      try {
        const values = JSON.parse(String(value));

        if (Array.isArray(values)) {
          return values.map((v, index) => (
            <div key={`${id}-${index}`} className="SmartFieldCellRenderer__Item">
              {getFieldRenderer({ fieldId: id, field, row, schemaFields, value: v })}
            </div>
          ));
        }
      } catch (e) {
        // do nothing, parse will throw for non JSON
      }

      return (
        <div key={id} className="SmartFieldCellRenderer__Item">
          {getFieldRenderer({ fieldId: id, field, row, schemaFields, value })}
        </div>
      );
    })
    .filter(exists)
    .flat();

  const content = [];

  for (let i = 0; i < MAX_ITEMS - 1; i++) {
    const renderer = renderers.shift();
    if (renderer) {
      content.push(renderer);
    } else {
      content.push(
        <div key={`empty-field-${i}`} className="SmartFieldCellRenderer__Item">
          <EmptyFieldRenderer />
        </div>,
      );
    }
  }

  if (renderers.length === 0) {
    content.push(
      <div key="empty-field-last" className="SmartFieldCellRenderer__Item">
        <EmptyFieldRenderer />
      </div>,
    );
  } else if (renderers.length === 1) {
    content.push(renderers.shift());
  } else {
    content.push(
      <div key={'more-field'} className="SmartFieldCellRenderer__Item">
        <span className="SmartFieldCellRenderer__Item--MoreItem">+{renderers.length} more</span>
      </div>,
    );
  }

  return <div className="SmartFieldCellRenderer">{content}</div>;
};
